import type { ChannelLinkDto } from "./protocol/types.js";

/**
 * `localStorage` under a namespaced key, scoped to one site (embeddable-widget skill: "No cookies
 * on the host domain, no fingerprinting, no reading anything the host page put in storage"). Every
 * key this widget ever touches is prefixed, so it can never collide with the host page's own use
 * of `localStorage` and never needs to read a key it didn't write itself.
 */
const PREFIX = "ago-chat";

function key(siteKey: string, name: string): string {
  return `${PREFIX}:${siteKey}:${name}`;
}

/**
 * `24-15`: what a tenant must be able to declare in their own cookie or privacy notice about what
 * this widget puts on a visitor's device - written for somebody drafting that notice, not for a
 * developer reading this file. **These are not cookies.** `localStorage`, under keys prefixed
 * `ago-chat:<siteKey>:` - a tenant who audits their site for cookies and finds none would otherwise,
 * wrongly, conclude there is nothing to declare.
 *
 * `key` is the suffix after that prefix - every other field is the fact the item asked for: what
 * the key holds, why the widget needs it, and the honest lifetime, including "nothing clears it"
 * where that is the true answer rather than leaving the reader to guess. `survivesTabClose` is `true`
 * on every row because `localStorage` always outlives the tab that wrote it - stated per row rather
 * than once, so the document built from this list never has to assume it.
 *
 * `WidgetStorageDisclosureTests` (`storage.disclosure.test.ts`) is the test `24-15` asked for: it
 * drives every public write path below against a real `localStorage` and asserts the key names
 * actually written are exactly this list's `key` fields (`last-sequence:<conversationId>` matched as
 * a prefix, since it is templated - one physical key per conversation, not one key overall) - so a
 * key added to `writeSafe`/`removeSafe` without a row here fails a test, not a review.
 *
 * `ago-console`'s tenant-facing document (`DeviceStorageDisclosurePage`) renders this same list,
 * translated and formatted for a tenant. It is a hand-maintained copy, not an import: the two
 * repositories build and deploy independently and neither consumes the other's source, so there is
 * no mechanical link between this array and that page's own copy of it - keep the two in step by eye
 * whenever this list changes, the same way `ago-console`'s own `DeviceStorageDisclosurePage.test.tsx`
 * documents the constraint on its side.
 */
export interface StorageDisclosureEntry {
  /** The key suffix, after `ago-chat:<siteKey>:`. */
  key: string;
  /** What the key holds, in plain language a tenant's privacy notice can use directly. */
  holds: string;
  /** Why the widget needs it. */
  why: string;
  /** The honest lifetime: real expiry, renewal, or "nothing clears it" stated as such. */
  lifetime: string;
  /** Whether the record survives the visitor closing the tab. Always `true` for `localStorage`. */
  survivesTabClose: true;
}

export const WIDGET_STORAGE_DISCLOSURE: readonly StorageDisclosureEntry[] = [
  {
    key: "visitor-token",
    holds: "A signed session token (JWT) proving this browser belongs to a specific visitor.",
    why: "Presented on every realtime connection and attachment request so the server recognises the same visitor across page loads.",
    lifetime:
      "Issued for 7 days and renewed automatically while the visitor keeps returning (`17-07`/`17-08`, `adr/0048`), so a returning visitor's token effectively never expires. If the visitor never returns, nothing clears the stored value.",
    survivesTabClose: true,
  },
  {
    key: "visitor-id",
    holds: "The visitor's own identifier, matching the token's `sub` claim.",
    why: "Lets the widget recognise the same visitor across a reload without asking the server first, so a returning visitor resumes their own conversation instead of starting a new one.",
    lifetime:
      "Renewed alongside the token above; replaced only if the server refuses to renew (an expired or rotated-out token forces a fresh identity, `17-07`). Otherwise nothing clears it.",
    survivesTabClose: true,
  },
  {
    key: "widget-color",
    holds: "The tenant's configured accent colour for the widget, if they set one.",
    why: "Lets the widget render with the tenant's chosen colour immediately on the next page load, before the server confirms it again.",
    lifetime:
      "Refreshed at least once a day for a returning visitor, and sooner if the identity token above " +
      "is itself due for renewal (`25-05`); removed the moment the tenant unsets the colour - a cache " +
      "of that one setting, not a record about the visitor.",
    survivesTabClose: true,
  },
  {
    key: "widget-position",
    holds: "The tenant's configured on-screen corner for the widget.",
    why: "Same purpose as the colour above - a cached rendering preference, refreshed with the session.",
    lifetime: "Same as the colour above.",
    survivesTabClose: true,
  },
  {
    key: "widget-channel-switcher-placement",
    holds: "The tenant's configured placement for the connected-channels display - either the above-composer card or the below-launcher row of icons (`25-173`).",
    why: "Same purpose as the corner above - a cached rendering preference, refreshed with the session.",
    lifetime: "Same as the colour above.",
    survivesTabClose: true,
  },
  {
    key: "widget-channel-switcher-icon-size",
    holds: "The tenant's configured icon size for the below-launcher channel row (`25-173`) - large, medium or small.",
    why: "Same purpose as the corner above - a cached rendering preference, refreshed with the session.",
    lifetime: "Same as the colour above.",
    survivesTabClose: true,
  },
  {
    key: "widget-locale",
    holds: "The tenant's configured widget language.",
    why: "Same purpose as the colour above.",
    lifetime: "Same as the colour above.",
    survivesTabClose: true,
  },
  {
    key: "widget-notice-text",
    holds: "The tenant's own processing-notice text (`16-04`) - words the tenant wrote, not the widget's own.",
    why: "Lets the widget show the tenant's notice without a second round trip once the session is cached.",
    lifetime: "Same as the colour above.",
    survivesTabClose: true,
  },
  {
    key: "widget-notice-url",
    holds: "A link to the tenant's own privacy policy, alongside the notice text above.",
    why: "Same purpose as the notice text above.",
    lifetime: "Same as the colour above.",
    survivesTabClose: true,
  },
  {
    key: "enabled-modules",
    holds: "Which of the tenant's paid add-ons (for example, online booking) are switched on for this site.",
    why: "Lets the widget show or hide an add-on's entry point without asking the server again on every reload.",
    lifetime: "Refreshed at least once a day for a returning visitor, and sooner if the identity token above is itself due for renewal (`25-05`); removed entirely once the tenant has no add-on switched on.",
    survivesTabClose: true,
  },
  {
    key: "enabled-module-trigger-words",
    holds: "The real, currently-configured trigger word(s) for each add-on above - the tenant's own choice, never a value this widget invents.",
    why: "Lets an add-on's own entry point (for example, the booking chip) send the word the tenant actually configured, instead of a fixed one that may no longer mean anything on this site (`25-131`).",
    lifetime: "Same as the add-on list above; removed entirely once the tenant has no add-on switched on.",
    survivesTabClose: true,
  },
  {
    key: "channel-links",
    holds: "The tenant's own connected messaging channels (for example, Telegram or WhatsApp) and the public link to each - not a secret: the same links the channel-switcher card (`25-149`) offers a visitor to tap, cached alongside the rest of the site's config. Telegram's own link may carry a one-time linking code, minted for this specific visitor.",
    why: "Lets the widget show the channel-switcher card without a second round trip once the session is cached, the same purpose the add-on list above already serves.",
    lifetime: "Refreshed at least once a day for a returning visitor, and sooner if the identity token above is itself due for renewal (`25-05`); removed entirely once the tenant has no channel connected.",
    survivesTabClose: true,
  },
  {
    key: "widget-attract-attention",
    holds: "Whether the tenant turned on «Привлекать внимание» - the launcher drawing attention to itself while closed (`23-63`).",
    why: "Same purpose as the colour above - a cached rendering preference, refreshed with the session.",
    lifetime: "Same as the colour above; absent entirely (not written as `\"false\"`) whenever the tenant has not turned the setting on.",
    survivesTabClose: true,
  },
  {
    key: "widget-auto-open-enabled",
    holds: "Whether the tenant turned on «Раскрывать виджет автоматически» (`23-64`).",
    why: "Same purpose as the colour above - a cached rendering preference, refreshed with the session.",
    lifetime: "Same as the colour above; absent entirely whenever the tenant has not turned the setting on.",
    survivesTabClose: true,
  },
  {
    key: "widget-auto-open-delay-seconds",
    holds: "How long the widget waits before opening itself, in seconds - one of 15, 30, 45, 60, 90 or 120.",
    why: "Same purpose as the colour above.",
    lifetime: "Same as the colour above; absent whenever auto-open itself is off.",
    survivesTabClose: true,
  },
  {
    key: "widget-auto-open-greeting-text",
    holds: "The tenant's own auto-open greeting line - words the tenant wrote, not the widget's own.",
    why: "Same purpose as the notice text above - lets the widget draw the greeting without a second round trip once the session is cached.",
    lifetime: "Same as the colour above; absent whenever auto-open itself is off.",
    survivesTabClose: true,
  },
  {
    key: "widget-contact-capture-confirmation-text",
    holds: "The tenant's own contact-capture confirmation text (`25-129`) - words the tenant wrote, not the widget's own.",
    why: "Same purpose as the notice text above - lets the widget show the tenant's confirmation without a second round trip once the session is cached.",
    lifetime: "Same as the colour above.",
    survivesTabClose: true,
  },
  {
    key: "widget-panel-title",
    holds: "The tenant's own override for the chat panel's title (`25-210`), if they set one - words the tenant wrote, not the widget's own. Absent for a tenant who has not configured one; the widget then shows its own built-in default greeting, not stored here.",
    why: "Same purpose as the notice text above - lets the widget show the tenant's chosen title without a second round trip once the session is cached.",
    lifetime: "Same as the colour above.",
    survivesTabClose: true,
  },
  {
    key: "auto-open-greeting-shown",
    holds: "Whether this browser has already been shown the tenant's auto-open greeting (`23-64`) - a yes/no flag, never the greeting text itself.",
    why: "«Opening is once» - a returning visitor within the same identity is not shown the greeting a second time.",
    lifetime: "Cleared only when the stored visitor identity itself is replaced (`17-07`), the same event that clears the conversation cursor below - a new identity has not been shown anything yet. Otherwise nothing clears it.",
    survivesTabClose: true,
  },
  {
    key: "has-known-contact-detail",
    holds: "Whether this browser has ever successfully submitted the contact-capture form (`ui/contactCapture.ts`) for this visitor identity - a yes/no flag, never the name/phone/e-mail itself, which is recorded server-side and never cached here.",
    why: "`25-136`: a module task's own reply-capable step (e.g. a booking's service choice) is gated on a contact detail being on file - this is how a later step, or a later conversation under the same stored identity, knows not to show that gate again once an earlier one already cleared it.",
    lifetime:
      "Set the moment `submitContactCapture` succeeds, from any of its three entry points (the out-of-hours control, the online «Представиться…» link, or `25-136`'s own module-step gate). Cleared only when the stored visitor identity itself is replaced (`17-07`), the same event `auto-open-greeting-shown` above is cleared on - a new identity has not given anyone their details yet. Otherwise nothing clears it. **A client-only convenience, not the source of truth**: a contact detail the tenant's own operator enters directly in the console for this visitor is real on the server the moment it is written, but this flag only ever changes when *this browser* submits the widget's own form, so it cannot reflect that write - `25-136`'s own scope note names this as an accepted limitation of a client-side-only gate, not an oversight.",
    survivesTabClose: true,
  },
  {
    key: "channel-switcher-dismissed",
    holds: "Whether this browser has dismissed the channel-switcher card (`25-149`) for this visitor identity - a yes/no flag, never which channel (if any) it tapped.",
    why: "The card's own cadence, the author's own decision: it shows on every panel open until dismissed, never a one-time-per-visit reveal - this flag is what makes a dismissal stick past the page load it happened on, and past every later one, rather than reappearing on the visitor's very next open.",
    lifetime:
      "Set the moment the visitor taps «Написать в чат», or sends their first message through the ordinary composer - either counts as \"this visitor chose the in-page chat.\" Cleared only when the stored visitor identity itself is replaced (`17-07`), the same event `has-known-contact-detail` above is cleared on - a new identity has not dismissed anything yet. Otherwise nothing clears it.",
    survivesTabClose: true,
  },
  {
    key: "conversation-id",
    holds: "The id of the conversation this browser last held with the tenant.",
    why: "Lets a reload resume the same conversation instead of starting a new one.",
    lifetime:
      "Replaced when a later conversation starts; cleared when the stored visitor identity itself is replaced (`17-07`), because a new identity does not own the old conversation. Otherwise nothing clears it.",
    survivesTabClose: true,
  },
  {
    key: "last-sequence:<conversationId>",
    holds: "The highest message sequence number this browser has seen for one conversation - a cursor, never message text.",
    why: "Lets a dropped connection resume with only what it missed while it was gone, instead of the whole transcript again. `23-53`: a plain reload no longer uses this to ask for less - it always asks for the visitor's own history page, so reopening the widget never comes back empty.",
    lifetime:
      "One entry per conversation the browser has ever resumed. The entry for whichever conversation id was current is removed when the stored identity itself is replaced (`17-07`); an entry for an earlier, already-superseded conversation is not otherwise cleared.",
    survivesTabClose: true,
  },
  {
    key: "last-read-sequence:<conversationId>",
    holds: "The highest message sequence number this browser had actually seen the moment the visitor last opened the panel - a read watermark, never message text. Distinct from `last-sequence:<conversationId>` above, which advances on every message this browser receives whether or not the panel is open.",
    why: "`25-143`: lets the next page load ask the server how many messages have arrived since this visitor last looked, so the closed launcher's own unread badge (`25-141`) starts at the true count instead of zero on every reload.",
    lifetime:
      "Set every time `open()`/`triggerAutoOpen()`'s fallback branch clear the in-memory unread count, to whatever `last-sequence:<conversationId>` holds at that moment (`25-224`'s channel-switcher branch never touches unread count, so it never writes this either). The entry for whichever conversation id was current is removed when the stored identity itself is replaced (`17-07`), the same event `last-sequence:<conversationId>` above is cleared on; an entry for an earlier, already-superseded conversation is not otherwise cleared.",
    survivesTabClose: true,
  },
];

export interface VisitorSession {
  token: string;
  visitorId: string;
  /**
   * `11-03`: cached alongside the identity at mint time, from the same `POST /api/v1/visitor-sessions`
   * response - not fetched separately. `adr/0029`'s "read once, at bootstrap" model is about the
   * *visitor's* handshake, which only ever happens once per visitor identity (`session.ts`'s
   * `VisitorSessionManager`: re-minting on every page view would fragment one visitor into many).
   *
   * **`17-07` largely closed this, and `25-05` narrowed what it left.** The paragraph that stood
   * here said fixing it "needs a session endpoint that can return current config without minting a
   * new visitor", and that is exactly what `POST /api/v1/visitor-sessions/renew` is: it returns the
   * same response shape, so every renewal rewrites these two fields. `17-07` alone still bounded a
   * returning visitor's stale config to the *identity* token's own renewal window - up to `2/3` of
   * its 7-day lifetime, since that window only opens once a third of it remains - which is what
   * produced `25-05`: a visitor whose browser already held a live, out-of-window token (the ordinary
   * case for anyone reloading a page they had open before) saw **no refresh at all** on an ordinary
   * reload, only on `Clear site data` removing the stored token outright. `session.ts`'s
   * `isConfigStale`/`CONFIG_REFRESH_INTERVAL_MS` now renews on a day's own schedule, independent of
   * how much life the identity token has left - and `adr/0029`'s own stated limitation, that an
   * *already-open* tab does not update live without a reload at all, is still untouched by either.
   *
   * `null` for a session written before this field existed, or for a site with no override -
   * `ui/appearance.ts`'s `parseWidgetColor`/`parseWidgetPosition` treat both identically to "not set".
   */
  widgetPrimaryColorHex: string | null;
  widgetPosition: string | null;
  /** `11-10`: cached alongside color/position on the identical terms - the same `POST
   * /api/v1/visitor-sessions`(`/renew`) response, refreshed on the identical schedule (`25-05`).
   * `null` for a session written before this field existed, or for a site with no override -
   * `i18n/resolve.ts`'s `parseWidgetLocale` treats that identically to "not set" and falls back to
   * English. */
  widgetLocale: string | null;
  /** `16-04`: cached alongside the rest on the identical terms - the tenant's own processing-notice
   * text and link, refreshed on the identical schedule (`25-05`). `null` for a session written before
   * this field existed, or for a site that has not configured a notice - `ui/appearance.ts`'s
   * `parseNoticeText`/`parseNoticeUrl` treat that identically to "not set" and render nothing. */
  widgetNoticeText: string | null;
  widgetNoticeUrl: string | null;
  /**
   * `23-105`: cached alongside the rest on the identical terms - the raw module keys
   * `AuthEndpoints.VisitorSessionResponse.EnabledModules` (`ago-chat`) carried on the response that
   * minted or last renewed this session, refreshed on the identical schedule (`25-05`). `[]`, never
   * `null`, for a session written before this field existed or for a site with no grant at all - the
   * same "no booking is the honest default, not a guess" property `config.ts`'s own remarks give the
   * pre-`23-105` attribute this field replaces.
   */
  enabledModules: string[];
  /**
   * `25-131`: cached alongside the rest on the identical terms - a map from each of
   * `enabledModules`'s own keys to that module's own real, currently-configured trigger word(s), the
   * raw `AuthEndpoints.VisitorSessionResponse.EnabledModuleTriggerWords` (`ago-chat`) carried on the
   * response that minted or last renewed this session, refreshed on the identical schedule (`25-05`).
   * `{}`, never absent, for a session written before this field existed or for a site with no grant at
   * all - `session.ts`'s `store` already collapses the wire's optional field before this type ever
   * sees it, the same "no third state to represent here" reasoning `widgetAttractAttention`'s own
   * remarks give for its own boolean.
   */
  enabledModuleTriggerWords: Record<string, string[]>;
  /**
   * `25-148`/`25-149`: cached alongside the rest on the identical terms - the raw
   * `AuthEndpoints.VisitorSessionResponse.ChannelLinks` (`ago-chat`) carried on the response that
   * minted or last renewed this session, refreshed on the identical schedule (`25-05`). `[]`, never
   * absent, for a session written before this field existed or for a site with nothing connected - the
   * same "no card is the honest default, not a guess" property `enabledModules` already gives its own
   * feature. `ui/widget.ts`'s channel-switcher card is the one reader.
   */
  channelLinks: ChannelLinkDto[];
  /** `23-63`: cached alongside the rest on the identical terms - whether the tenant turned
   * «Привлекать внимание» on, refreshed on the identical schedule (`25-05`). Unlike the string fields
   * above, this one is a plain `boolean` rather than `T | null`: `session.ts`'s `store` already
   * collapses the wire's optional field to `false` before this type ever sees it, so there is no
   * third "not set" state left to represent here - `false` already means both "off" and "unknown". */
  widgetAttractAttention: boolean;
  /** `23-64`: cached alongside the rest on the identical terms - whether the tenant turned
   * «Раскрывать виджет автоматически» on, the delay, and the greeting text, refreshed on the
   * identical schedule (`25-05`). `widgetAutoOpenEnabled` is a plain `boolean` for the same reason
   * `widgetAttractAttention` already is - `session.ts`'s `store` collapses the wire's optional field
   * before this type ever sees it. `widgetAutoOpenDelaySeconds` falls back to `30`
   * (`Ago.Chat.Domain.AutoOpenDelay.Seconds30`), never `0` - a missing value must never schedule the
   * timer for "immediately". `widgetAutoOpenGreetingText` stays `T | null`, the same shape
   * `widgetNoticeText` already has: `null` for a session written before this field existed, or for a
   * site that has never configured one - `ui/appearance.ts`'s `parseAutoOpenGreetingText` treats both
   * identically to "no greeting". */
  widgetAutoOpenEnabled: boolean;
  widgetAutoOpenDelaySeconds: number;
  widgetAutoOpenGreetingText: string | null;
  /** `25-129`: cached alongside the rest on the identical terms `widgetNoticeText` already has -
   * the tenant's own contact-capture confirmation text, refreshed on the identical schedule
   * (`25-05`). `null` for a session written before this field existed, or for a site that has not
   * configured one - `ui/appearance.ts`'s `parseContactCaptureConfirmationText` treats both
   * identically to "not set", and `ui/widget.ts`'s `appendContactCaptureControl` falls back to the
   * widget's own default sentence. */
  widgetContactCaptureConfirmationText: string | null;
  /** `25-173`: cached alongside color/position on the identical terms `widgetPosition` already has -
   * the raw wire value, not yet normalised - refreshed on the identical schedule (`25-05`). `null` for
   * a session written before this field existed, or for a site that has never configured one -
   * `ui/appearance.ts`'s `parseChannelSwitcherPlacement`/`parseChannelSwitcherIconSize` treat both
   * identically to "not set" and fall back to `25-149`'s own pre-existing card at the default size. */
  widgetChannelSwitcherPlacement: string | null;
  widgetChannelSwitcherIconSize: string | null;
  /** `25-210`: cached alongside the rest on the identical terms `widgetNoticeText` already has - the
   * chat panel's own `<h1>` override, refreshed on the identical schedule (`25-05`). `null` for a
   * session written before this field existed, or for a site that has never configured one -
   * `ui/appearance.ts`'s `parsePanelTitle` treats both identically to "not set", and `ui/widget.ts`
   * falls back to the widget's own built-in default greeting, never to a blank title. */
  widgetPanelTitle: string | null;
}

export class WidgetStorage {
  constructor(private readonly siteKey: string) {}

  private readSafe(name: string): string | null {
    try {
      return localStorage.getItem(key(this.siteKey, name));
    } catch {
      // Private-browsing modes and disabled storage throw on access, not just on write - treated
      // the same as "nothing stored yet", never a reason to break the widget.
      return null;
    }
  }

  private writeSafe(name: string, value: string): void {
    try {
      localStorage.setItem(key(this.siteKey, name), value);
    } catch {
      // No storage available - the widget still works for this page load, it just can't resume a
      // conversation across a reload.
    }
  }

  private removeSafe(name: string): void {
    try {
      localStorage.removeItem(key(this.siteKey, name));
    } catch {
      // Same as writeSafe - storage unavailable is not this widget's problem to solve.
    }
  }

  /**
   * `26-271`: the raw cached accent colour alone, readable before a full `VisitorSession` exists to
   * ask for it - `ui/widget.ts`'s constructor calls this synchronously, before the handshake that
   * `getVisitorSession()`'s own `token`/`visitorId` fields would otherwise gate it behind, so a
   * returning visitor's launcher can paint in the tenant's real colour on its very first frame
   * instead of the widget's own built-in default blue. `null` for a site that has never configured
   * one, or that this browser has not cached yet - the identical "not set" answer `getVisitorSession`
   * gives the same key.
   */
  getCachedWidgetColor(): string | null {
    return this.readSafe("widget-color");
  }

  getVisitorSession(): VisitorSession | null {
    const token = this.readSafe("visitor-token");
    const visitorId = this.readSafe("visitor-id");
    if (!token || !visitorId) {
      return null;
    }

    return {
      token,
      visitorId,
      widgetPrimaryColorHex: this.readSafe("widget-color"),
      widgetPosition: this.readSafe("widget-position"),
      widgetLocale: this.readSafe("widget-locale"),
      widgetNoticeText: this.readSafe("widget-notice-text"),
      widgetNoticeUrl: this.readSafe("widget-notice-url"),
      enabledModules: this.readEnabledModulesSafe(),
      enabledModuleTriggerWords: this.readEnabledModuleTriggerWordsSafe(),
      channelLinks: this.readChannelLinksSafe(),
      widgetAttractAttention: this.readSafe("widget-attract-attention") === "true",
      widgetAutoOpenEnabled: this.readSafe("widget-auto-open-enabled") === "true",
      widgetAutoOpenDelaySeconds: this.readAutoOpenDelaySecondsSafe(),
      widgetAutoOpenGreetingText: this.readSafe("widget-auto-open-greeting-text"),
      widgetContactCaptureConfirmationText: this.readSafe("widget-contact-capture-confirmation-text"),
      widgetChannelSwitcherPlacement: this.readSafe("widget-channel-switcher-placement"),
      widgetChannelSwitcherIconSize: this.readSafe("widget-channel-switcher-icon-size"),
      widgetPanelTitle: this.readSafe("widget-panel-title"),
    };
  }

  /** `23-64`: mirrors `getLastKnownSequence`'s own `Number.isFinite` fallback - a corrupted or absent
   * stored value degrades to the server-side default (`Ago.Chat.Domain.AutoOpenDelay.Seconds30`),
   * never `0`/`NaN`, which `scheduleAutoOpen` would otherwise read as "open immediately". */
  private readAutoOpenDelaySecondsSafe(): number {
    const raw = this.readSafe("widget-auto-open-delay-seconds");
    if (raw === null) {
      return 30;
    }

    const parsed = Number.parseInt(raw, 10);
    return Number.isFinite(parsed) ? parsed : 30;
  }

  /**
   * `23-105`: the one cached field here shaped as a list rather than a scalar, so it is stored as a
   * JSON array under its own key rather than joined into a delimited string - `ModuleKey`'s own
   * charset (`ago-chat`'s `Domain/ModuleKey.cs`) excludes a comma, but this class has no reason to
   * depend on a server-side constraint it does not otherwise read. `[]` for anything unreadable or
   * unparsable - a corrupted value degrades to "no booking", never a thrown exception on the host
   * page, the same posture `getLastKnownSequence`'s own `Number.isFinite` fallback already takes for
   * its own stored field.
   */
  private readEnabledModulesSafe(): string[] {
    const raw = this.readSafe("enabled-modules");
    if (raw === null) {
      return [];
    }

    try {
      const parsed: unknown = JSON.parse(raw);
      return Array.isArray(parsed) && parsed.every((entry) => typeof entry === "string") ? parsed : [];
    } catch {
      return [];
    }
  }

  /**
   * `25-131`: the identical "corrupted or unparsable degrades to the honest empty default" posture
   * `readEnabledModulesSafe` above already takes, for the map beside it - a malformed value here must
   * never surface as a trigger word the tenant did not actually configure. Each entry is re-validated
   * on its own: one module's own malformed array is dropped without discarding every other module's
   * perfectly good one.
   */
  private readEnabledModuleTriggerWordsSafe(): Record<string, string[]> {
    const raw = this.readSafe("enabled-module-trigger-words");
    if (raw === null) {
      return {};
    }

    try {
      const parsed: unknown = JSON.parse(raw);
      if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
        return {};
      }

      const result: Record<string, string[]> = {};
      for (const [moduleKey, triggerWords] of Object.entries(parsed as Record<string, unknown>)) {
        if (Array.isArray(triggerWords) && triggerWords.every((word) => typeof word === "string")) {
          result[moduleKey] = triggerWords;
        }
      }
      return result;
    } catch {
      return {};
    }
  }

  /**
   * `25-148`/`25-149`: the identical "corrupted or unparsable degrades to the honest empty default"
   * posture `readEnabledModulesSafe`/`readEnabledModuleTriggerWordsSafe` above already take - a
   * malformed value here must never surface as a channel link the tenant did not actually connect.
   * Each entry is re-validated on its own shape (`{kind: string, url: string}`); one malformed entry
   * is dropped without discarding every other, perfectly good one.
   */
  private readChannelLinksSafe(): ChannelLinkDto[] {
    const raw = this.readSafe("channel-links");
    if (raw === null) {
      return [];
    }

    try {
      const parsed: unknown = JSON.parse(raw);
      if (!Array.isArray(parsed)) {
        return [];
      }

      return parsed.filter(
        (entry): entry is ChannelLinkDto =>
          typeof entry === "object" &&
          entry !== null &&
          typeof (entry as ChannelLinkDto).kind === "string" &&
          typeof (entry as ChannelLinkDto).url === "string",
      );
    } catch {
      return [];
    }
  }

  setVisitorSession(session: VisitorSession): void {
    this.writeSafe("visitor-token", session.token);
    this.writeSafe("visitor-id", session.visitorId);

    // Written as separate keys, matching every other value this class stores - only written when
    // present, so a stale key from a differently-configured site never lingers past an update.
    if (session.widgetPrimaryColorHex) {
      this.writeSafe("widget-color", session.widgetPrimaryColorHex);
    } else {
      this.removeSafe("widget-color");
    }

    if (session.widgetPosition) {
      this.writeSafe("widget-position", session.widgetPosition);
    } else {
      this.removeSafe("widget-position");
    }

    if (session.widgetLocale) {
      this.writeSafe("widget-locale", session.widgetLocale);
    } else {
      this.removeSafe("widget-locale");
    }

    if (session.widgetNoticeText) {
      this.writeSafe("widget-notice-text", session.widgetNoticeText);
    } else {
      this.removeSafe("widget-notice-text");
    }

    if (session.widgetNoticeUrl) {
      this.writeSafe("widget-notice-url", session.widgetNoticeUrl);
    } else {
      this.removeSafe("widget-notice-url");
    }

    if (session.widgetContactCaptureConfirmationText) {
      this.writeSafe("widget-contact-capture-confirmation-text", session.widgetContactCaptureConfirmationText);
    } else {
      this.removeSafe("widget-contact-capture-confirmation-text");
    }

    // `25-210`: the identical "written only when present" shape `widgetNoticeText` already has above -
    // absent whenever the tenant has not configured an override, so the widget falls back to its own
    // built-in default greeting rather than reading a stale value from an earlier site configuration.
    if (session.widgetPanelTitle) {
      this.writeSafe("widget-panel-title", session.widgetPanelTitle);
    } else {
      this.removeSafe("widget-panel-title");
    }

    // `25-173`: the identical "written only when present" shape `widgetPosition` already has above.
    if (session.widgetChannelSwitcherPlacement) {
      this.writeSafe("widget-channel-switcher-placement", session.widgetChannelSwitcherPlacement);
    } else {
      this.removeSafe("widget-channel-switcher-placement");
    }

    if (session.widgetChannelSwitcherIconSize) {
      this.writeSafe("widget-channel-switcher-icon-size", session.widgetChannelSwitcherIconSize);
    } else {
      this.removeSafe("widget-channel-switcher-icon-size");
    }

    // `23-105`: written only when non-empty, matching every optional field above - a site with no
    // grant leaves no trace of this key rather than storing an empty array under it.
    if (session.enabledModules.length > 0) {
      this.writeSafe("enabled-modules", JSON.stringify(session.enabledModules));
    } else {
      this.removeSafe("enabled-modules");
    }

    // `25-131`: the identical "written only when non-empty" shape immediately above, for the map
    // beside it - a site with no grant at all (or none carrying a trigger word) leaves no trace of
    // this key either.
    if (Object.keys(session.enabledModuleTriggerWords).length > 0) {
      this.writeSafe("enabled-module-trigger-words", JSON.stringify(session.enabledModuleTriggerWords));
    } else {
      this.removeSafe("enabled-module-trigger-words");
    }

    // `25-148`/`25-149`: the identical "written only when non-empty" shape immediately above - a site
    // with nothing connected leaves no trace of this key either.
    if (session.channelLinks.length > 0) {
      this.writeSafe("channel-links", JSON.stringify(session.channelLinks));
    } else {
      this.removeSafe("channel-links");
    }

    // `23-63`: only written when `true`, matching every other field above - a tenant who has never
    // turned the setting on, or who turned it back off, leaves no stale key behind.
    if (session.widgetAttractAttention) {
      this.writeSafe("widget-attract-attention", "true");
    } else {
      this.removeSafe("widget-attract-attention");
    }

    // `23-64`: the same "only written when it means something" shape as `widgetAttractAttention`
    // just above - all three fields absent together whenever the tenant has not turned auto-open on,
    // never a lone delay or greeting surviving a tenant switching it back off.
    if (session.widgetAutoOpenEnabled) {
      this.writeSafe("widget-auto-open-enabled", "true");
      this.writeSafe("widget-auto-open-delay-seconds", String(session.widgetAutoOpenDelaySeconds));
      if (session.widgetAutoOpenGreetingText) {
        this.writeSafe("widget-auto-open-greeting-text", session.widgetAutoOpenGreetingText);
      } else {
        this.removeSafe("widget-auto-open-greeting-text");
      }
    } else {
      this.removeSafe("widget-auto-open-enabled");
      this.removeSafe("widget-auto-open-delay-seconds");
      this.removeSafe("widget-auto-open-greeting-text");
    }
  }

  getLastKnownSequence(conversationId: string): number | null {
    const raw = this.readSafe(`last-sequence:${conversationId}`);
    if (raw === null) {
      return null;
    }

    const parsed = Number.parseInt(raw, 10);
    return Number.isFinite(parsed) ? parsed : null;
  }

  setLastKnownSequence(conversationId: string, sequence: number): void {
    this.writeSafe(`last-sequence:${conversationId}`, String(sequence));
  }

  /**
   * `25-143`: the read watermark the reload-time unread-count seed reads back on the *next* page load
   * - `null` for a conversation this browser has never opened the panel on (a first-ever visit, or a
   * conversation resumed from an even earlier one whose own watermark this method's own key namespace
   * never carried over), which is exactly the "omit `afterSequence` entirely" case the new endpoint's
   * own contract asks for.
   */
  getLastReadSequence(conversationId: string): number | null {
    const raw = this.readSafe(`last-read-sequence:${conversationId}`);
    if (raw === null) {
      return null;
    }

    const parsed = Number.parseInt(raw, 10);
    return Number.isFinite(parsed) ? parsed : null;
  }

  /** Called only from `open()`/`triggerAutoOpen()`'s fallback branch (`ui/widget.ts`), the same two places that
   * reset the in-memory `unreadCount` to zero - `sequence` is the latest sequence this browser has
   * actually seen at that exact moment, read from `getLastKnownSequence` above rather than tracked a
   * second time: `connection.ts`'s `rememberSequence` already writes that key through to storage,
   * synchronously, every time `VisitorConnection`'s own `SequenceTracker` advances (on the initial
   * history page, a live push, or a reconnect's delta alike), so it is current before, during and
   * after this page load's own hub connection ever exists - unlike a value read off the connection
   * object itself, which would answer "nothing yet" for the entire window before `open()`'s own lazy
   * `connect()` call has resolved. */
  setLastReadSequence(conversationId: string, sequence: number): void {
    this.writeSafe(`last-read-sequence:${conversationId}`, String(sequence));
  }

  /**
   * `23-64`/`adr/0148`: «Opening is once» - the backlog item's own scope, and the visitor's stored
   * identity is "the visitor's own session" this item asks to reuse rather than inventing a second
   * lifetime (its own words: "the visitor's own session already has a lifetime and reusing it is the
   * obvious answer"). A plain boolean, not a timestamp - there is no decaying window to compute, only
   * "has this identity already seen it".
   */
  getAutoOpenGreetingShown(): boolean {
    return this.readSafe("auto-open-greeting-shown") === "true";
  }

  setAutoOpenGreetingShown(): void {
    this.writeSafe("auto-open-greeting-shown", "true");
  }

  /** `17-07`: the identical event `clearConversation` already exists for - a stored identity the
   * server would no longer renew being replaced by a freshly minted one. A new `VisitorId` has not
   * been shown anything yet, so this flag must not survive onto it the way it would if left alone
   * (every other cached config field is fine to survive, since it is overwritten by the next `store`
   * call regardless - this one is never overwritten by a normal session refresh, only ever set once
   * by `ui/widget.ts` when the greeting is actually drawn, so it needs its own explicit clear). */
  clearAutoOpenGreetingShown(): void {
    this.removeSafe("auto-open-greeting-shown");
  }

  /**
   * `25-136`: the identical "opening is once" shape `getAutoOpenGreetingShown` already has, for a
   * different fact - whether this browser's visitor identity has ever successfully submitted the
   * contact-capture form. `ui/widget.ts`'s `submitContactCapture` is the one place that ever calls
   * `setHasKnownContactDetail`, regardless of which of its three callers triggered the submission, so
   * a module step's own gate and the out-of-hours control share one flag rather than each tracking its
   * own.
   */
  getHasKnownContactDetail(): boolean {
    return this.readSafe("has-known-contact-detail") === "true";
  }

  setHasKnownContactDetail(): void {
    this.writeSafe("has-known-contact-detail", "true");
  }

  /** `17-07`: cleared on the identical event `clearAutoOpenGreetingShown` already is - a freshly
   * minted identity has not given anyone their details yet, so this flag must not survive onto it. */
  clearHasKnownContactDetail(): void {
    this.removeSafe("has-known-contact-detail");
  }

  /**
   * `25-149`: the identical "opening is once - unless it's this instead" shape `getHasKnownContactDetail`
   * already has, for a different fact - whether this browser's visitor identity has already dismissed
   * the channel-switcher card. `ui/widget.ts`'s `dismissChannelSwitcher` used to be the one place that
   * ever called `setChannelSwitcherDismissed`, regardless of which of its two callers (the card's own
   * «Написать в чат» row, or the visitor's first sent message) triggered it - both counted as "chose
   * the in-page chat," so neither tracked its own separate flag.
   *
   * `25-204`: that card (`loadChannelSwitcherCard`) and `dismissChannelSwitcher` with it were retired
   * outright - the `AboveComposer` placement is now a purely hover-driven floating banner with no
   * persisted "seen once" memory at all (`buildChannelSwitcherBanner`'s own doc comment has the
   * reasoning). Nothing in `ui/widget.ts` calls `setChannelSwitcherDismissed`/`getChannelSwitcherDismissed`
   * any more; these two, and `clearChannelSwitcherDismissed` below, are exercised only by this file's
   * own tests now, plus `VisitorSessionManager.start`'s `17-07` identity-reset sweep, which clears every
   * per-identity flag unconditionally regardless of whether the widget still reads it.
   */
  getChannelSwitcherDismissed(): boolean {
    return this.readSafe("channel-switcher-dismissed") === "true";
  }

  setChannelSwitcherDismissed(): void {
    this.writeSafe("channel-switcher-dismissed", "true");
  }

  /** `17-07`: cleared on the identical event `clearAutoOpenGreetingShown`/`clearHasKnownContactDetail`
   * already are - a freshly minted identity has not dismissed anything yet, so this flag must not
   * survive onto it. */
  clearChannelSwitcherDismissed(): void {
    this.removeSafe("channel-switcher-dismissed");
  }

  getConversationId(): string | null {
    return this.readSafe("conversation-id");
  }

  setConversationId(conversationId: string): void {
    this.writeSafe("conversation-id", conversationId);
  }

  /**
   * `17-07`: forgets which conversation this browser was resuming, and the cursor into it.
   *
   * Called on exactly one event - a stored visitor identity the server would no longer renew being
   * replaced by a freshly minted one (`session.ts`'s `start`). The new `VisitorId` does not own the
   * old conversation, and leaving the cursor behind would make the first `JoinAsync` of the new
   * session ask to resume *from a sequence in somebody else's transcript*: the server answers with
   * the delta after that sequence in the conversation it actually resolves for this visitor, so a
   * new conversation whose own messages sit below that number comes back empty and the visitor
   * watches their own messages fail to appear.
   *
   * Removes the cursor before the id, because the cursor's key is derived from the id.
   *
   * `25-143`: `last-read-sequence:<conversationId>` is removed alongside `last-sequence:<conversationId>`
   * for the identical reason - a new `VisitorId` has not opened this conversation's panel either, so a
   * watermark from the conversation the old identity owned must not seed the new identity's own badge.
   */
  clearConversation(): void {
    const conversationId = this.getConversationId();
    if (conversationId !== null) {
      this.removeSafe(`last-sequence:${conversationId}`);
      this.removeSafe(`last-read-sequence:${conversationId}`);
    }

    this.removeSafe("conversation-id");
  }
}
